import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, 
  Users, 
  Mail, 
  Phone, 
  User, 
  UserPlus, 
  UserCheck, 
  UserX, 
  Shield, 
  MessageCircle,
  MoreVertical,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  X
} from 'lucide-react';
import PrivacyService, { UserSearchResult, UserConnection } from '../../services/privacy';
import { useAuth } from '../../context/AuthContext';

interface UserSearchProps {
  onClose: () => void;
}

const UserSearch: React.FC<UserSearchProps> = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'all' | 'username' | 'email' | 'phone' | 'display_name'>('all');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [friendRequests, setFriendRequests] = useState<UserConnection[]>([]);
  const [friendSuggestions, setSuggestions] = useState<UserSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [activeTab, setActiveTab] = useState<'search' | 'requests' | 'suggestions'>('search');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadFriendRequests();
    loadSuggestions();
  }, []);

  const loadFriendRequests = async () => {
    try {
      const requests = await PrivacyService.getUserConnections('pending');
      setFriendRequests(requests);
    } catch (error) {
      console.error('Error loading friend requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const suggestions = await PrivacyService.getFriendSuggestions(10);
      setSuggestions(suggestions);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const results = await PrivacyService.searchUsers(searchQuery.trim(), searchType, 20);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
      setMessage({ type: 'error', text: 'Failed to search users' });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchType]);

  useEffect(() => {
    const debounceTimer = setTimeout(handleSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [handleSearch]);

  const handleSendFriendRequest = async (userId: string) => {
    try {
      const result = await PrivacyService.sendFriendRequest(userId);
      if (result.success) {
        setMessage({ type: 'success', text: result.message || 'Friend request sent!' });
        // Update the search results to reflect the new connection status
        setSearchResults(prev => 
          prev.map(user => 
            user.user_id === userId 
              ? { ...user, connection_status: 'pending' }
              : user
          )
        );
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send friend request' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send friend request' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleRespondToRequest = async (requesterId: string, response: 'accept' | 'decline') => {
    try {
      const result = await PrivacyService.respondToFriendRequest(requesterId, response);
      if (result.success) {
        setMessage({ type: 'success', text: result.message || `Friend request ${response}ed!` });
        // Reload friend requests to update the list
        await loadFriendRequests();
      } else {
        setMessage({ type: 'error', text: result.error || `Failed to ${response} friend request` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to ${response} friend request` });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleBlockUser = async (userId: string) => {
    try {
      const result = await PrivacyService.toggleUserBlock(userId, 'block');
      if (result.success) {
        setMessage({ type: 'success', text: result.message || 'User blocked successfully' });
        // Remove from search results
        setSearchResults(prev => prev.filter(user => user.user_id !== userId));
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to block user' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to block user' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const UserCard = ({ user, isRequest = false }: {
    user: UserSearchResult
    isRequest?: boolean
  }) => {
    const [showMenu, setShowMenu] = useState(false);
    
    // Type guard to check if it's a UserConnection
    const isUserConnection = (u: any): u is UserConnection => {
      return 'requester_id' in u && 'addressee_id' in u;
    };

    const displayUser = isUserConnection(user) ? {
      user_id: user.requester_id,
      username: user.requester?.username || 'Unknown',
      display_name: user.requester?.display_name || 'Unknown',
      email: null,
      avatar_url: user.requester?.avatar_url || null,
      is_verified: false,
      connection_status: user.status as any,
      mutual_connections_count: 0
    } : user;

    const getConnectionButton = () => {
      if (isRequest) {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => handleRespondToRequest(displayUser.user_id, 'accept')}
              className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1"
            >
              <CheckCircle className="h-4 w-4" />
              <span>Accept</span>
            </button>
            <button
              onClick={() => handleRespondToRequest(displayUser.user_id, 'decline')}
              className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
            >
              <XCircle className="h-4 w-4" />
              <span>Decline</span>
            </button>
          </div>
        );
      }

      switch (displayUser.connection_status) {
        case 'none':
          return (
            <button
              onClick={() => handleSendFriendRequest(displayUser.user_id)}
              className="px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-1"
            >
              <UserPlus className="h-4 w-4" />
              <span>Connect</span>
            </button>
          );
        case 'pending':
          return (
            <div className="flex items-center space-x-1 text-yellow-600">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Pending</span>
            </div>
          );
        case 'accepted':
          return (
            <div className="flex items-center space-x-1 text-green-600">
              <UserCheck className="h-4 w-4" />
              <span className="text-sm">Friends</span>
            </div>
          );
        case 'declined':
          return (
            <div className="flex items-center space-x-1 text-red-600">
              <UserX className="h-4 w-4" />
              <span className="text-sm">Declined</span>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
            {displayUser.avatar_url ? (
              <img 
                src={displayUser.avatar_url} 
                alt={displayUser.username}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <User className="h-6 w-6 text-white" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="font-medium text-gray-900">{displayUser.display_name}</h3>
              {displayUser.is_verified && (
                <CheckCircle className="h-4 w-4 text-blue-500" />
              )}
            </div>
            <p className="text-sm text-gray-500">@{displayUser.username}</p>
            {displayUser.email && (
              <p className="text-xs text-gray-400">{displayUser.email}</p>
            )}
            {displayUser.mutual_connections_count > 0 && (
              <p className="text-xs text-purple-600">
                {displayUser.mutual_connections_count} mutual connection{displayUser.mutual_connections_count !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getConnectionButton()}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <button
                  onClick={() => {
                    // Handle view profile
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  View Profile
                </button>
                <button
                  onClick={() => {
                    // Handle send message
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Send Message</span>
                </button>
                <hr className="my-1" />
                <button
                  onClick={() => {
                    handleBlockUser(displayUser.user_id);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                >
                  <Shield className="h-4 w-4" />
                  <span>Block User</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Search className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Find People</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 ${message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-b`}>
            <div className="flex items-center space-x-2">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              <span className={`text-sm font-medium ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {message.text}
              </span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('search')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'search'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Search Users
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-6 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'requests'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Friend Requests
            {friendRequests.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {friendRequests.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'suggestions'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Suggestions
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'search' && (
            <div className="p-6">
              {/* Search Controls */}
              <div className="mb-6 space-y-4">
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name, username, email, or phone..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Fields</option>
                    <option value="username">Username</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="display_name">Display Name</option>
                  </select>
                </div>
              </div>

              {/* Search Results */}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Searching...</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Search Results ({searchResults.length})</h3>
                  {searchResults.map((user) => (
                    <UserCard key={user.user_id} user={user} />
                  ))}
                </div>
              ) : searchQuery ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No users found matching your search.</p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Start typing to search for users...</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="p-6">
              {loadingRequests ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading requests...</span>
                </div>
              ) : friendRequests.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Friend Requests ({friendRequests.length})</h3>
                  {friendRequests.map((request) => (
                    <UserCard 
                      key={request.id} 
                      user={request} 
                      isRequest={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No pending friend requests.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'suggestions' && (
            <div className="p-6">
              {friendSuggestions.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">People You May Know</h3>
                  {friendSuggestions.map((user) => (
                    <UserCard key={user.user_id} user={user} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No suggestions available at the moment.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSearch; 